import {
  Button,
  DropdownMenu,
  Icon,
  IconButton,
  SplitButton,
  Tooltip,
} from '@kontentino/ui';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faClock } from '@fortawesome/pro-regular-svg-icons/faClock';
import PermissionsGateButton from 'app/components/PermissionsGateButton';
import { UserPermission } from 'constants/userPermission';
import React, { FC } from 'react';
import { PermissionsGate } from '@kontentino/react-permissions-gate';
import PageTypeUtils from 'app/utils/pageType';
import PostTypeUtils from 'app/utils/postType';
import { useTranslation } from 'react-i18next';
import { ScheduleMethodType } from 'app/modules/posts/types/ScheduleMethodType';
import { getUniquePrimitiveItemsFromArray } from 'utils/helpers';
import { SUBTYPE as POST_SUBTYPE } from '@kontentino/kontentino-constants/Posts';
import {
  areOptionsEnabled,
  getScheduleOptionsByPageType,
} from 'app/modules/posts/modules/detail/helpers/scheduleWorkflowButtonHelpers';

type Post = {
  type: number;
  subtype: number;
  page: {
    type: number;
  };
};

type Props = {
  post: Post | Post[];
  onSchedule: (type?: ScheduleMethodType) => Promise<void> | void;
  isDisabled: boolean;
  isLoading?: boolean;
  schedulingOptions: {
    option: ScheduleMethodType;
    disabled: boolean;
  }[];
  scheduleButtonTooltipContent?: string;
};

const ScheduleWorkflowButton: FC<Props> = ({
  post,
  onSchedule,
  isDisabled,
  schedulingOptions,
  isLoading,
  scheduleButtonTooltipContent,
}) => {
  const { t } = useTranslation();
  const posts = Array.isArray(post) ? post : [post];

  const dataSocialMedia = getUniquePrimitiveItemsFromArray(
    posts.map((post) => PageTypeUtils.getName(post.page.type)),
  ).join(',');
  const dataPostType = getUniquePrimitiveItemsFromArray(
    posts.map((post) => PostTypeUtils.getExactName(post.type, post.subtype)),
  ).join(',');

  if (areOptionsEnabled(posts)) {
    const allowedOptions = schedulingOptions.filter(({ option }) => {
      return !(
        (option === 'exportInAdvance' || option === 'createDarkPost') &&
        posts.some((post) =>
          (
            [POST_SUBTYPE.REEL, POST_SUBTYPE.INSTAGRAM_STORY] as number[]
          ).includes(post.subtype),
        )
      );
    });
    const hasValidOptions = allowedOptions.some(({ option }) => {
      const optionConfig =
        getScheduleOptionsByPageType()[posts[0].page.type][option];
      return !!optionConfig;
    });

    if (allowedOptions.length > 0 && hasValidOptions) {
      return (
        <PermissionsGate scopes={UserPermission.POST_SCHEDULE}>
          <SplitButton
            variant="success"
            dropdownProps={{
              side: 'top',
            }}
            button={
              <Tooltip content={scheduleButtonTooltipContent}>
                <Button
                  onClick={() => onSchedule()}
                  iconBefore={<Icon icon={faClock} />}
                  disabled={isLoading || isDisabled}
                  data-name="posts-preview-schedule"
                  data-cy="post-schedule-button"
                  data-social-media={dataSocialMedia}
                  data-post-type={dataPostType}
                  isLoading={isLoading}
                  variant={'success'}
                  className="tw-rounded-r-none"
                >
                  {t('schedule')}
                </Button>
              </Tooltip>
            }
            dropdownButton={
              <IconButton
                data-social-media={dataSocialMedia}
                data-name="posts-preview-schedule-more"
                data-cy="post-schedule-dropdown-button"
                icon={<Icon icon={faChevronDown} />}
                disabled={isLoading}
              />
            }
          >
            {allowedOptions.map(({ option, disabled }) => {
              const optionConfig =
                getScheduleOptionsByPageType()[posts[0].page.type][option];

              if (!optionConfig) {
                return null;
              }

              const content = (
                <DropdownMenu.Item
                  data-name={optionConfig?.dataName}
                  data-cy={optionConfig?.dataCy}
                  onSelect={() => onSchedule(option)}
                  disabled={disabled}
                >
                  {optionConfig?.icon && <Icon icon={optionConfig.icon} />}
                  {t(option)}
                  {optionConfig?.infoIcon && (
                    <Tooltip
                      content={t(optionConfig.infoIcon.tooltip)}
                      className="tw-text-center"
                    >
                      <Icon icon={optionConfig.infoIcon.icon} size="sm" />
                    </Tooltip>
                  )}
                </DropdownMenu.Item>
              );

              return optionConfig?.disabledTooltip ? (
                <Tooltip
                  key={option}
                  content={
                    !isDisabled && disabled
                      ? t(optionConfig.disabledTooltip)
                      : undefined
                  }
                >
                  <div>{content}</div>
                </Tooltip>
              ) : (
                <React.Fragment key={option}>{content}</React.Fragment>
              );
            })}
          </SplitButton>
        </PermissionsGate>
      );
    }
  }

  return (
    <PermissionsGateButton
      isLoading={isLoading}
      variant="success"
      scopes={UserPermission.POST_SCHEDULE}
      iconBefore={<Icon icon={faClock} />}
      onClick={() => onSchedule()}
      data-name="posts-preview-schedule"
      data-cy="post-schedule-button"
      data-social-media={dataSocialMedia}
      data-post-type={dataPostType}
      tooltipContent={scheduleButtonTooltipContent}
      disabled={isDisabled}
    >
      {t('schedule')}
    </PermissionsGateButton>
  );
};

export default ScheduleWorkflowButton;
